import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Building2, Mail, Phone, Globe } from 'lucide-react';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Button } from '../components/ui/Button';
import { toast } from 'react-hot-toast';

const contactSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  email: z.string().email('Invalid email address'),
  company: z.string().optional(),
  phone: z.string().optional(),
  subject: z.string().min(1, 'Subject is required'),
  message: z.string().min(10, 'Message must be at least 10 characters'),
  requestType: z.string().min(1, 'Please select a request type'),
});

type ContactFormData = z.infer<typeof contactSchema>;

const requestTypes = [
  { key: 'business', value: 'business', label: 'Business proposal' },
  { key: 'advertising', value: 'advertising', label: 'Advertising proposal' },
  { key: 'development', value: 'development', label: 'Development request' },
  { key: 'software', value: 'software', label: 'Software request' },
  { key: 'feature', value: 'feature', label: 'Feature request' },
  { key: 'bug', value: 'bug', label: 'Bug or software problem' },
];

export default function AboutPage() {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema),
  });

  const onSubmit = async (data: ContactFormData) => {
    try {
      // Simulate form submission
      await new Promise(resolve => setTimeout(resolve, 1000));
      console.log('Form submitted:', data);
      toast.success('Message sent successfully!');
      reset();
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      {/* About Us Card */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">About Us</h2>
        <p className="text-gray-600 mb-6">
          Ezboss Recipe is a cutting-edge recipe management platform designed by Ezboss, LLC to help food enthusiasts
          organize, discover, and share their favorite recipes. Our mission is to make cooking more
          accessible and enjoyable for everyone, from beginners to professional chefs.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-center gap-3 text-gray-600">
            <Building2 className="h-5 w-5 text-indigo-600" />
            <div>
              <p className="font-medium">Ezboss, LLC.</p>
              <p className="text-sm">14907 Coldwater Ln.</p>
              <p className="text-sm">Tampa, FL 33624</p>
            </div>
          </div>
          
          <div className="space-y-2">
            <a href="mailto:ezboss.business@gmail.com" className="flex items-center gap-3 text-gray-600 hover:text-indigo-600">
              <Mail className="h-5 w-5" />
              <span>ezboss.business@gmail.com</span>
            </a>
            
            <a href="tel:+18133358410" className="flex items-center gap-3 text-gray-600 hover:text-indigo-600">
              <Phone className="h-5 w-5" />
              <span>+1 (813) 335-8410</span>
            </a>
            
            <a href="https://www.ezbossdev.com" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-gray-600 hover:text-indigo-600">
              <Globe className="h-5 w-5" />
              <span>www.ezbossdev.com</span>
            </a>
          </div>
        </div>
      </div>

      {/* Contact Form Card */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Contact Us</h2>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Input
              label="Name"
              placeholder="Your name"
              {...register('name')}
              error={errors.name?.message}
              required
            />
            
            <Input
              label="Email"
              type="email"
              placeholder="your.email@example.com"
              {...register('email')}
              error={errors.email?.message}
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Input
              label="Company"
              placeholder="Your company (optional)"
              {...register('company')}
            />
            
            <Input
              label="Phone"
              type="tel"
              placeholder="Your phone number (optional)"
              {...register('phone')}
            />
          </div>

          <Select
            label="Request Type"
            options={requestTypes}
            {...register('requestType')}
            error={errors.requestType?.message}
          />

          <Input
            label="Subject"
            placeholder="Message subject"
            {...register('subject')}
            error={errors.subject?.message}
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Message
            </label>
            <textarea
              {...register('message')}
              rows={5}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              placeholder="Your message..."
            ></textarea>
            {errors.message && (
              <p className="mt-1 text-sm text-red-600">{errors.message.message}</p>
            )}
          </div>

          <div className="flex justify-end">
            <Button type="submit" isLoading={isSubmitting}>
              Send Message
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}